import { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { baseApiURL } from "../../baseUrl";
import { sortEnrollmentNo } from "../../utils/enrollmentSorter";
import { FiAlertCircle } from "react-icons/fi";

const ViewTotalAttendance = ({ branch: lockedBranch }) => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [noStudentsMessage, setNoStudentsMessage] = useState("");
  const [enrollmentNumbers, setEnrollmentNumbers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [students, setStudents] = useState([]); // Added to get student section data

  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedBranch, setSelectedBranch] = useState(lockedBranch || "");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [selectedRegulation, setSelectedRegulation] = useState("");
  const [studentRegulations, setStudentRegulations] = useState([]);
  const [selectedSection, setSelectedSection] = useState("");
  const [enrollmentSearch, setEnrollmentSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [sections, setSections] = useState(['A', 'B', 'C', 'D']);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        let params = {};
        if (selectedBranch && selectedBranch !== "-- Select --") params.branch = selectedBranch;
        if (selectedSemester && selectedSemester !== "-- Select --") params.semester = selectedSemester;
        const res = await axios.get(`${baseApiURL()}/section/getSectionsByBranchAndSemester`, { params });
        if (res.data.success && res.data.sections?.length > 0) {
          setSections(res.data.sections);
        }
      } catch (err) {
        console.error("Error fetching dynamic sections:", err);
      }
    };
    fetchSections();
  }, [selectedBranch, selectedSemester]);

  useEffect(() => {
    const fetchStudentRegulations = async () => {
      try {
        const response = await axios.get(`${baseApiURL()}/student/details/getRegulations`);
        if (response.data.success) {
          setStudentRegulations(response.data.regulations || []);
        }
      } catch (error) {
        console.error("Error fetching student regulations:", error);
      }
    };
    fetchStudentRegulations();
  }, []);

  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        const response = await axios.get(`${baseApiURL()}/attendence/getAll`);
        if (response.data.success) {
          setAttendanceRecords(response.data.attendance);

          const uniqueEnrollments = [
            ...new Set(response.data.attendance.map((item) => item.enrollmentNo)),
          ];
          
          uniqueEnrollments.sort(sortEnrollmentNo);
          
          setEnrollmentNumbers(uniqueEnrollments);
        } else {
          setError(response.data.message);
        }
      } catch (err) {
        setError("Failed to fetch attendance records. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    const fetchBranches = async () => {
      try {
        const response = await axios.get(`${baseApiURL()}/branch/getBranch`);
        if (response.data.success) {
          const branchNames = response.data.branches.map((b) => b.name);
          setBranches(branchNames);
        }
      } catch (err) {
        console.error("Error fetching branches:", err);
      }
    };

    const fetchStudents = async () => {
      try {
        const response = await axios.get(`${baseApiURL()}/student/details/getDetails2`);
        if (response.data.success) {
          setStudents(response.data.students);
        }
      } catch (err) {
        console.error("Error fetching students:", err);
      }
    };

    fetchAttendance();
    fetchBranches();
    fetchStudents();
  }, []);

  useEffect(() => {
    const fetchSubjectData = async () => {
      try {
        const response = await axios.get(`${baseApiURL()}/subject/getSubject`);
        if (response.data && response.data.success) {
          setAllSubjects(response.data.subject);
        }
      } catch (error) {
        console.error("Error fetching subject totals:", error);
      }
    };
    fetchSubjectData();
  }, []);

  useEffect(() => {
    const filterSubjectsByStudentRegulation = async () => {
      if (selectedBranch && selectedSemester) {
        let studentReg = null;
        let hasStudents = false;
        try {
          const studentRes = await axios.post(`${baseApiURL()}/student/details/getDetails`, {
            branch: selectedBranch,
            semester: Number(selectedSemester)
          });
          if (studentRes.data.success && studentRes.data.user && studentRes.data.user.length > 0) {
            hasStudents = true;
            studentReg = studentRes.data.user[0].regulation;
          }
        } catch (err) {
          console.warn("Could not fetch students for regulation check:", err);
        }

        if (!hasStudents) {
          setFilteredSubjects([]);
          setNoStudentsMessage("no students are there for that semester");
          return;
        }

        setNoStudentsMessage("");

        const filtered = allSubjects.filter(
          (subject) =>
            (subject.branch?.name === selectedBranch || subject.branch === selectedBranch) &&
            String(subject.semester) === String(selectedSemester) &&
            (!studentReg || subject.regulation?.toUpperCase() === studentReg.toUpperCase())
        );
        setFilteredSubjects(filtered);
      } else {
        setFilteredSubjects([]);
        setNoStudentsMessage("");
      }
    };
    filterSubjectsByStudentRegulation();
  }, [selectedBranch, selectedSemester, allSubjects]);

  // Function to get section-specific total for a subject
  const getSectionTotal = (subjectName, studentSection) => {
    if (!allSubjects || !Array.isArray(allSubjects)) return 0;
    
    const subject = allSubjects.find(sub => 
      sub.name === subjectName
    );
    
    if (!subject || !subject.sectionTotals) return 0;
    
    const sectionData = subject.sectionTotals.find(s => s.section === studentSection);
    return sectionData ? sectionData.total : 0;
  };

  // Function to get student section from student data
  const getStudentSection = (enrollmentNo) => {
    const student = students.find(s => s.enrollmentNo === enrollmentNo);
    return student ? student.section : '';
  };

  // Calculate student subject summary with section-specific totals
  const calculateStudentSummary = () => {
    if (attendanceRecords.length === 0 || allSubjects.length === 0) {
      return {};
    }

    const summary = {};

    attendanceRecords.forEach((record) => {
      const { enrollmentNo, subject, branch, semester, section } = record;
      const student = students.find(s => s.enrollmentNo === enrollmentNo);
      const studentSection = student ? student.section : '';
      const studentSemester = student ? student.semester : '';
      
      // Ignore attendance records that do not match the student's current semester
      if (studentSemester && String(semester) !== String(studentSemester)) {
        return;
      }
      
      if (
        (!selectedBranch || branch === selectedBranch) &&
        (!selectedSemester || String(semester) === String(selectedSemester)) &&
        (!selectedSection || section === selectedSection) &&
        (!selectedSection || studentSection === selectedSection) // Filter by student's actual section
      ) {
        if (!summary[enrollmentNo]) {
          summary[enrollmentNo] = {
            branch: branch,
            semester: semester,
            section: studentSection, // Use student's actual section
            subjects: {}
          };
        }
        
        if (!summary[enrollmentNo].subjects[subject]) {
          summary[enrollmentNo].subjects[subject] = 0;
        }
        summary[enrollmentNo].subjects[subject] += 1;
      }
    });

    // Calculate percentages with section-specific totals
    Object.keys(summary).forEach((enrollmentNo) => {
      const studentData = summary[enrollmentNo];
      let totalAttendedAllSubjects = 0;
      let totalClassesAllSubjects = 0;

      Object.keys(studentData.subjects).forEach((subject) => {
        const attended = studentData.subjects[subject];
        // Use section-specific total
        const total = getSectionTotal(subject, studentData.section);
        const percentage = total > 0 ? ((attended / total) * 100).toFixed(2) : 0;

        totalAttendedAllSubjects += attended;
        totalClassesAllSubjects += total;

        studentData.subjects[subject] = {
          attended,
          total,
          percentage,
        };
      });

      studentData.overallTotal = {
        attended: totalAttendedAllSubjects,
        total: totalClassesAllSubjects,
        percentage:
          totalClassesAllSubjects > 0
            ? ((totalAttendedAllSubjects / totalClassesAllSubjects) * 100).toFixed(2)
            : 0,
      };
    });

    return summary;
  };

  const studentSubjectSummary = calculateStudentSummary();

  const handleSubjectChange = (e) => {
    setSelectedSubject(e.target.value);
  };

  const handleBranchChange = (e) => {
    setSelectedBranch(e.target.value);
    setSelectedSubject(""); // Reset subject when branch changes
  };

  const handleSemesterChange = (e) => {
    setSelectedSemester(e.target.value);
    setSelectedSubject(""); // Reset subject when semester changes
  };

  const handleSectionChange = (e) => {
    setSelectedSection(e.target.value);
  };

  const handleExportToExcel = () => {
    const dataToExport = [];

    Object.entries(studentSubjectSummary).forEach(([enrollmentNo, studentData]) => {
      const { branch, semester, section, subjects, overallTotal } = studentData;

      // Add subject-wise records
      Object.entries(subjects).forEach(([subject, data]) => {
        dataToExport.push({
          "Enrollment No": enrollmentNo,
          "Branch": branch,
          "Semester": semester,
          "Section": section,
          "Subject": subject,
          "Attended Classes": data.attended,
          "Total Classes": data.total,
          "Subject Percentage": `${data.percentage}%`,
          "Total Attendance Percentage": "N/A",
        });
      });

      // Add overall total record
      if (overallTotal) {
        dataToExport.push({
          "Enrollment No": enrollmentNo,
          "Branch": branch,
          "Semester": semester,
          "Section": section,
          "Subject": "TOTAL",
          "Attended Classes": overallTotal.attended,
          "Total Classes": overallTotal.total,
          "Subject Percentage": "N/A",
          "Total Attendance Percentage": `${overallTotal.percentage}%`,
        });
      }
    });

    const filteredData = dataToExport
      .filter(
        (item) =>
          (selectedSubject ? item.Subject === selectedSubject : true) &&
          (enrollmentSearch
            ? item["Enrollment No"].toLowerCase().includes(enrollmentSearch.toLowerCase())
            : true) &&
          (selectedBranch ? item.Branch === selectedBranch : true) &&
          (selectedSemester ? item.Semester === parseInt(selectedSemester) : true) &&
          (selectedSection ? item.Section === selectedSection : true)
      )
      .sort(sortEnrollmentNo);

    if (filteredData.length === 0) {
      alert("No data to export for the selected filters.");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance Report");
    
    // Create filename with filter information
    let filename = "Attendance_Report";
    if (selectedBranch) filename += `_${selectedBranch}`;
    if (selectedSemester) filename += `_Sem${selectedSemester}`;
    if (selectedSection) filename += `_Sec${selectedSection}`;
    filename += ".xlsx";
    
    XLSX.writeFile(wb, filename);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400 mb-3"></div>
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Loading attendance records...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-2xl text-xs">
        <strong className="font-bold">Error: </strong>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Information Banner */}
      <div className="bg-indigo-50 dark:bg-slate-950 border border-indigo-100 dark:border-slate-800 rounded-2xl p-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <p className="text-slate-700 dark:text-slate-300 text-xs">
            <strong>Note:</strong> Total classes are section-specific. Each section has its own total class count.
          </p>
        </div>
      </div>
      
      {/* Filters Section */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Branch</label>
            <select
              value={selectedBranch}
              onChange={handleBranchChange}
              disabled={!!lockedBranch}
              className={`w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white ${lockedBranch ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option value="">All Branches</option>
              {branches.map((branch) => (
                <option key={branch} value={branch}>
                  {branch}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Semester</label>
            <select
              value={selectedSemester}
              onChange={handleSemesterChange}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            >
              <option value="">All Semesters</option>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
                <option key={sem} value={sem}>
                  Semester {sem}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Regulation</label>
            <input
              type="text"
              value={selectedRegulation}
              readOnly
              placeholder="Auto-fetched..."
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 cursor-not-allowed font-bold text-indigo-600 dark:text-indigo-400"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Section</label>
            <select
              value={selectedSection}
              onChange={handleSectionChange}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            >
              <option value="">All Sections</option>
              {sections.map((section) => (
                <option key={section} value={section}>
                  Section {section}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Subject</label>
            <select
              value={selectedSubject}
              onChange={handleSubjectChange}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
              disabled={!selectedBranch || !selectedSemester}
            >
              <option value="">All Subjects</option>
              {filteredSubjects.map((subject) => (
                <option key={subject._id} value={subject.name}>
                  {subject.name} {subject.code ? `(${subject.code})` : ''}
                </option>
              ))}
            </select>
            {noStudentsMessage ? (
              <div className="mt-1.5 p-1.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded text-amber-800 dark:text-amber-300 text-[10px] flex items-center space-x-1">
                <FiAlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                <span>{noStudentsMessage}</span>
              </div>
            ) : null}
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Search Enrollment</label>
            <input
              type="text"
              value={enrollmentSearch}
              onChange={(e) => setEnrollmentSearch(e.target.value)}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
              placeholder="Search..."
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={handleExportToExcel}
            disabled={Object.keys(studentSubjectSummary).length === 0}
            className={`font-bold text-xs py-2 px-4 rounded-xl transition flex items-center justify-center cursor-pointer ${
              Object.keys(studentSubjectSummary).length === 0
                ? 'bg-slate-200 dark:bg-slate-800 cursor-not-allowed text-slate-400'
                : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-xs'
            }`}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            Export to Excel
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
            <thead className="bg-slate-50 dark:bg-slate-950">
              <tr>
                <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Enrollment No</th>
                <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Branch</th>
                <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Semester</th>
                <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Section</th>
                <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Subject</th>
                <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Attended</th>
                <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Total</th>
                <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Subject %</th>
                <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Overall %</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800 text-xs">
              {Object.entries(studentSubjectSummary)
                .flatMap(([enrollmentNo, studentData]) => {
                  const { branch, semester, section, subjects, overallTotal } = studentData;
                  const rows = [];
                  
                  Object.entries(subjects).forEach(([subject, data]) => {
                    rows.push({
                      enrollmentNo,
                      branch,
                      semester,
                      section,
                      subject,
                      attended: data.attended,
                      total: data.total,
                      subjectPercentage: data.percentage,
                      totalAttendancePercentage: "N/A",
                      isTotalRow: false,
                    });
                  });

                  if (overallTotal) {
                    rows.push({
                      enrollmentNo,
                      branch,
                      semester,
                      section,
                      subject: "TOTAL",
                      attended: overallTotal.attended,
                      total: overallTotal.total,
                      subjectPercentage: "N/A",
                      totalAttendancePercentage: overallTotal.percentage,
                      isTotalRow: true,
                    });
                  }
                  
                  return rows;
                })
                .filter(
                  (item) =>
                    (selectedSubject ? item.subject === selectedSubject : true) &&
                    (enrollmentSearch
                      ? item.enrollmentNo.toLowerCase().includes(enrollmentSearch.toLowerCase())
                      : true) &&
                    (selectedBranch ? item.branch === selectedBranch : true) &&
                    (selectedSemester ? item.semester === parseInt(selectedSemester) : true) &&
                    (selectedSection ? item.section === selectedSection : true)
                )
                .sort((a, b) => {
                  const enrollmentCompare = sortEnrollmentNo(a.enrollmentNo, b.enrollmentNo);
                  if (enrollmentCompare !== 0) return enrollmentCompare;
                  if (a.isTotalRow !== b.isTotalRow) {
                    return a.isTotalRow ? 1 : -1;
                  }
                  return a.subject.localeCompare(b.subject);
                })
                .map((item, index) => (
                  <tr
                    key={index}
                    className={
                      item.isTotalRow
                        ? "bg-indigo-50/60 dark:bg-indigo-950/40 font-bold text-indigo-900 dark:text-indigo-200"
                        : "hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-800 dark:text-slate-200"
                    }
                  >
                    <td className="px-5 py-3.5 whitespace-nowrap font-mono">{item.enrollmentNo}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap">{item.branch}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap">{item.semester}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap">{item.section}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap font-medium">{item.subject}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap font-bold">{item.attended}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap font-bold">{item.total}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap font-bold text-indigo-600 dark:text-indigo-400">
                      {item.subjectPercentage !== "N/A" ? `${item.subjectPercentage}%` : "N/A"}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap font-black text-emerald-600 dark:text-emerald-400">
                      {item.totalAttendancePercentage !== "N/A" ? `${item.totalAttendancePercentage}%` : "N/A"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        
        {Object.keys(studentSubjectSummary).length === 0 && (
          <div className="text-center py-8 text-slate-400 text-xs font-medium">
            No attendance records found for the selected filters.
          </div>
        )}
      </div>

      {/* Summary Statistics */}
      {Object.keys(studentSubjectSummary).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl shadow-xs text-center">
            <div className="text-2xl font-black text-indigo-600 dark:text-indigo-400">{Object.keys(studentSubjectSummary).length}</div>
            <div className="text-xs font-bold text-slate-500 dark:text-slate-400 mt-0.5">Total Students</div>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl shadow-xs text-center">
            <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
              {new Set(Object.values(studentSubjectSummary).map(s => s.branch)).size}
            </div>
            <div className="text-xs font-bold text-slate-500 dark:text-slate-400 mt-0.5">Branches</div>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl shadow-xs text-center">
            <div className="text-2xl font-black text-purple-600 dark:text-purple-400">
              {new Set(Object.entries(studentSubjectSummary).flatMap(([_, data]) => 
                Object.keys(data.subjects)
              )).size}
            </div>
            <div className="text-xs font-bold text-slate-500 dark:text-slate-400 mt-0.5">Subjects</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewTotalAttendance;